import { c as _c } from "react/compiler-runtime";
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { DataStatus, useTranslationContext } from 'platform/components';
import { useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import { isNilOrEmpty, isNotNil } from 'ramda-adjunct';
import { useOnMount } from 'shared/web';
import { useHttpCalls } from './api/useHttpCalls';
import type { DataGridContext } from './context/DataGridContext';
import { DataGridContextProvider } from './context/useDataGridContext';
import { DataGridErrorContextProvider } from './context/useDataGridErrorContext';
import { EagGridTable } from './EagGridTable';
import { useAvailableHeightPx } from './hooks/useAvailableHeightPx';
import { useAvailableWidthPx } from './hooks/useAvailableWidthPx';
import { useFilterHydration } from './hooks/useFilterHydration';
import { useIsMobileView } from './hooks/useIsMobileView';
import { useLoadPresets } from './hooks/useLoadPresets';
import { selectActivePreset, selectApiError, selectCustomPresets, selectDataQuery, selectDefinition, selectVirtualPreset } from './store/createEagGridStore';
import { EagGridStoreProvider } from './store/EagGridStoreProvider';
import { selectWrapperStyle } from './store/selectors/useBehavior';
import { useEagGridSelector } from './store/useEagGridSelector';
import { useEagGridStore } from './store/useEagGridStore';
import type { ActionCallback } from './types/ActionCallback';
import type { EagGridProps } from './types/EagGridProps';
import type { EagGridRef } from './types/EagGridRef';
import { getApiErrorMessage } from './utils/getApiErrorMessage';
import { getContainerStyle, getWrapperClassName } from './utils/getContainerStyle';
import { transformDefinition } from './utils/transformDefinition';
import { getSearchParamsByGridCode } from './utils/url/getSearchParamsByGridCode';
import { setSearchParamsByGridCode } from './utils/url/setSearchParamsByGridCode';
const PREVENT_CONTEXT_MENU = (e: MouseEvent) => {
  e.preventDefault();
};
const DESKTOP_BOTTOM_INSET_PX = 16;
function EagGridInner(props) {
  const $ = _c(98);
  const t = useTranslationContext();
  const innerRef = useRef(null);
  const store = useEagGridStore();
  const isMobile = useIsMobileView();
  const t0 = isMobile ? 0 : DESKTOP_BOTTOM_INSET_PX;
  let t1;
  if ($[0] !== t0) {
    t1 = {
      bottomInset: t0
    };
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    availableHeightPx,
    callbackRef
  } = useAvailableHeightPx(t1);
  const {
    availableWidthPx,
    callbackRef: widthCallbackRef
  } = useAvailableWidthPx();
  let t2;
  if ($[2] !== props.disableAllActions || $[3] !== props.gridCode || $[4] !== props.presetInstanceId || $[5] !== store) {
    t2 = () => {
      store.initStaticConfig({
        gridCode: props.gridCode,
        presetInstanceId: props.presetInstanceId,
        disableAllActions: props.disableAllActions
      });
    };
    $[2] = props.disableAllActions;
    $[3] = props.gridCode;
    $[4] = props.presetInstanceId;
    $[5] = store;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  useOnMount(t2);
  const dataQuery = useEagGridSelector(selectDataQuery);
  const customPresets = useEagGridSelector(selectCustomPresets);
  const virtualPreset = useEagGridSelector(selectVirtualPreset);
  const definition = useEagGridSelector(selectDefinition);
  const activePreset = useEagGridSelector(selectActivePreset);
  const apiError = useEagGridSelector(selectApiError);
  const wrapperStyle = useEagGridSelector(selectWrapperStyle);
  let t3;
  if ($[7] !== store || $[8] !== t) {
    t3 = error => {
      const errorMessage = getApiErrorMessage(error, t("general.notifications.defaultErrorMessage"));
      store.setApiError(errorMessage);
    };
    $[7] = store;
    $[8] = t;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const onApiError = t3;
  let t4;
  if ($[10] !== dataQuery || $[11] !== props.initialSmartSearch || $[12] !== props.queryModifier || $[13] !== store) {
    t4 = {
      dataQuery,
      queryModifier: props.queryModifier,
      initialSmartSearch: props.initialSmartSearch,
      store
    };
    $[10] = dataQuery;
    $[11] = props.initialSmartSearch;
    $[12] = props.queryModifier;
    $[13] = store;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const isFilterHydrated = useFilterHydration(t4);
  const isDatagridLoading = !virtualPreset || !definition || !activePreset || !dataQuery || !isFilterHydrated || isNotNil(apiError);
  let t5;
  if ($[15] !== onApiError || $[16] !== props.gridCode || $[17] !== props.presetInstanceId) {
    t5 = {
      gridCode: props.gridCode,
      presetInstanceId: props.presetInstanceId,
      onApiError
    };
    $[15] = onApiError;
    $[16] = props.gridCode;
    $[17] = props.presetInstanceId;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  const http = useHttpCalls(t5);
  let t6;
  if ($[19] !== onApiError || $[20] !== props.gridCode || $[21] !== props.presetInstanceId || $[22] !== props.queryModifier) {
    t6 = {
      gridCode: props.gridCode,
      presetInstanceId: props.presetInstanceId,
      onApiError,
      queryModifier: props.queryModifier
    };
    $[19] = onApiError;
    $[20] = props.gridCode;
    $[21] = props.presetInstanceId;
    $[22] = props.queryModifier;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  const loadPresets = useLoadPresets(t6);
  const loadPresetsRef = useRef(loadPresets);
  const httpRef = useRef(http);
  const externalFilterResourceIdRef = useRef(props.externalFilterResourceId);
  const externalFilterRecordIdRef = useRef(props.externalFilterRecordId);
  const onApiErrorRef = useRef(onApiError);
  let t7;
  if ($[24] !== http || $[25] !== loadPresets || $[26] !== onApiError || $[27] !== props.externalFilterRecordId || $[28] !== props.externalFilterResourceId) {
    t7 = () => {
      loadPresetsRef.current = loadPresets;
      httpRef.current = http;
      externalFilterResourceIdRef.current = props.externalFilterResourceId;
      externalFilterRecordIdRef.current = props.externalFilterRecordId;
      onApiErrorRef.current = onApiError;
    };
    $[24] = http;
    $[25] = loadPresets;
    $[26] = onApiError;
    $[27] = props.externalFilterRecordId;
    $[28] = props.externalFilterResourceId;
    $[29] = t7;
  } else {
    t7 = $[29];
  }
  useEffect(t7);
  let t8;
  if ($[30] !== store) {
    t8 = () => {
      loadPresetsRef.current().then(response => response && store.setCustomPresets(response));
      httpRef.current.getVirtualPreset().then(response_0 => response_0 && store.setVirtualPreset(response_0));
      httpRef.current.getDefinition({
        loadChoiceFilterOptions: false
      }).then(response_1 => {
        if (!response_1) {
          return;
        }
        store.setDefinition(response_1);
      });
    };
    $[30] = store;
    $[31] = t8;
  } else {
    t8 = $[31];
  }
  let t9;
  if ($[32] !== props.gridCode || $[33] !== store) {
    t9 = [props.gridCode, store];
    $[32] = props.gridCode;
    $[33] = store;
    $[34] = t9;
  } else {
    t9 = $[34];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[35] !== customPresets || $[36] !== props.gridCode || $[37] !== store || $[38] !== virtualPreset) {
    t10 = () => {
      if (virtualPreset && customPresets) {
        const currentSearchParams = getSearchParamsByGridCode(props.gridCode);
        httpRef.current.getActivePreset(virtualPreset, customPresets).then(resolvedActivePreset => {
          if (!resolvedActivePreset) {
            return;
          }
          store.setActivePreset(resolvedActivePreset);
          if (isNilOrEmpty(currentSearchParams)) {
            setSearchParamsByGridCode(props.gridCode, resolvedActivePreset.dataQueryId);
          }
          httpRef.current.getDataQuery(currentSearchParams?.queryId ?? resolvedActivePreset.dataQueryId).then(resolvedDataQuery => resolvedDataQuery && store.setDataQuery(resolvedDataQuery)).catch(error_0 => {
            if (isNotNil(currentSearchParams?.queryId)) {
              setSearchParamsByGridCode(props.gridCode, resolvedActivePreset.dataQueryId);
              httpRef.current.getDataQuery(resolvedActivePreset.dataQueryId).then(resolvedDataQuery_0 => resolvedDataQuery_0 && store.setDataQuery(resolvedDataQuery_0)).catch(onApiErrorRef.current);
            } else {
              onApiErrorRef.current(error_0);
            }
          });
        });
      }
    };
    t11 = [customPresets, virtualPreset, store, props.gridCode];
    $[35] = customPresets;
    $[36] = props.gridCode;
    $[37] = store;
    $[38] = virtualPreset;
    $[39] = t10;
    $[40] = t11;
  } else {
    t10 = $[39];
    t11 = $[40];
  }
  useEffect(t10, t11);
  let t12;
  if ($[41] !== props.externalFilterId || $[42] !== props.filterComponent || $[43] !== props.onExternalFilterChange || $[44] !== props.queryModifier) {
    t12 = {
      externalFilterId: props.externalFilterId,
      onExternalFilterChange: props.onExternalFilterChange,
      queryModifier: props.queryModifier,
      filterComponent: props.filterComponent
    };
    $[41] = props.externalFilterId;
    $[42] = props.filterComponent;
    $[43] = props.onExternalFilterChange;
    $[44] = props.queryModifier;
    $[45] = t12;
  } else {
    t12 = $[45];
  }
  const contextValue = t12;
  let t13;
  if ($[46] !== onApiError) {
    t13 = {
      onApiError
    };
    $[46] = onApiError;
    $[47] = t13;
  } else {
    t13 = $[47];
  }
  const errorContextValue = t13;
  const dataTestId = props["data-testid"];
  let t14;
  if ($[48] !== activePreset || $[49] !== dataQuery || $[50] !== dataTestId || $[51] !== definition) {
    t14 = definition && activePreset && dataQuery ? transformDefinition({
      definition,
      activePreset,
      dataQuery,
      "data-testid": dataTestId
    }) : null;
    $[48] = activePreset;
    $[49] = dataQuery;
    $[50] = dataTestId;
    $[51] = definition;
    $[52] = t14;
  } else {
    t14 = $[52];
  }
  const columnConfig = t14;
  const wrapperNodeRef = useRef(null);
  const [parentHeight, setParentHeight] = useState(0);
  let t15;
  if ($[53] !== callbackRef || $[54] !== widthCallbackRef) {
    t15 = node => {
      wrapperNodeRef.current = node;
      setParentHeight(node?.parentElement?.offsetHeight ?? 0);
      callbackRef(node);
      widthCallbackRef(node);
    };
    $[53] = callbackRef;
    $[54] = widthCallbackRef;
    $[55] = t15;
  } else {
    t15 = $[55];
  }
  const composedCallbackRef = t15;
  const t16 = props.autoHeight ?? false;
  let t17;
  if ($[56] !== availableHeightPx || $[57] !== availableWidthPx || $[58] !== parentHeight || $[59] !== t16) {
    t17 = getContainerStyle(t16, availableHeightPx, parentHeight, availableWidthPx);
    $[56] = availableHeightPx;
    $[57] = availableWidthPx;
    $[58] = parentHeight;
    $[59] = t16;
    $[60] = t17;
  } else {
    t17 = $[60];
  }
  const containerStyle = t17;
  if (isNotNil(apiError)) {
    const t18 = props.autoHeight ? 20 : "100%";
    let t19;
    if ($[61] !== apiError || $[62] !== t18) {
      t19 = <DataStatus isError={true} errorMessage={apiError} minHeight={t18} />;
      $[61] = apiError;
      $[62] = t18;
      $[63] = t19;
    } else {
      t19 = $[63];
    }
    return t19;
  }
  if (isDatagridLoading || !definition || !columnConfig) {
    const t18 = props.autoHeight ? 20 : "100%";
    let t19;
    if ($[64] !== t18) {
      t19 = <DataStatus isLoading={true} minHeight={t18} />;
      $[64] = t18;
      $[65] = t19;
    } else {
      t19 = $[65];
    }
    return t19;
  }
  const t18 = props.autoHeight ?? false;
  let t19;
  if ($[66] !== isMobile || $[67] !== t18 || $[68] !== wrapperStyle) {
    t19 = getWrapperClassName(wrapperStyle, t18, isMobile);
    $[66] = isMobile;
    $[67] = t18;
    $[68] = wrapperStyle;
    $[69] = t19;
  } else {
    t19 = $[69];
  }
  const t20 = props.actionCallback as ActionCallback;
  const t21 = props["data-testid"];
  let t22;
  if ($[70] !== props.predefinedColumns) {
    t22 = props.predefinedColumns ?? [];
    $[70] = props.predefinedColumns;
    $[71] = t22;
  } else {
    t22 = $[71];
  }
  let t23;
  if ($[72] !== columnConfig || $[73] !== definition || $[74] !== props.autoHeight || $[75] !== props.emptyState || $[76] !== props.externalFilterId || $[77] !== props.gridCode || $[78] !== props.onFilterChanged || $[79] !== props.onRowSelectionChange || $[80] !== props.presetInstanceId || $[81] !== props.queryModifier || $[82] !== store.setVirtualPreset || $[83] !== t20 || $[84] !== t21 || $[85] !== t22) {
    t23 = <EagGridTable ref={innerRef} gridCode={props.gridCode} presetInstanceId={props.presetInstanceId} emptyState={props.emptyState} actionCallback={t20} onRowSelectionChange={props.onRowSelectionChange} externalFilterId={props.externalFilterId} autoHeight={props.autoHeight} queryModifier={props.queryModifier} definition={definition} columnConfig={columnConfig} onFilterChanged={props.onFilterChanged} data-testid={t21} setVirtualPreset={store.setVirtualPreset} predefinedColumns={t22} />;
    $[72] = columnConfig;
    $[73] = definition;
    $[74] = props.autoHeight;
    $[75] = props.emptyState;
    $[76] = props.externalFilterId;
    $[77] = props.gridCode;
    $[78] = props.onFilterChanged;
    $[79] = props.onRowSelectionChange;
    $[80] = props.presetInstanceId;
    $[81] = props.queryModifier;
    $[82] = store.setVirtualPreset;
    $[83] = t20;
    $[84] = t21;
    $[85] = t22;
    $[86] = t23;
  } else {
    t23 = $[86];
  }
  let t24;
  if ($[87] !== contextValue || $[88] !== t23) {
    t24 = <DataGridContextProvider value={contextValue}>{t23}</DataGridContextProvider>;
    $[87] = contextValue;
    $[88] = t23;
    $[89] = t24;
  } else {
    t24 = $[89];
  }
  let t25;
  if ($[90] !== errorContextValue || $[91] !== t24) {
    t25 = <DataGridErrorContextProvider value={errorContextValue}>{t24}</DataGridErrorContextProvider>;
    $[90] = errorContextValue;
    $[91] = t24;
    $[92] = t25;
  } else {
    t25 = $[92];
  }
  let t26;
  if ($[93] !== composedCallbackRef || $[94] !== containerStyle || $[95] !== t19 || $[96] !== t25) {
    t26 = <div ref={composedCallbackRef} className={t19} style={containerStyle} onContextMenu={PREVENT_CONTEXT_MENU}>{t25}</div>;
    $[93] = composedCallbackRef;
    $[94] = containerStyle;
    $[95] = t19;
    $[96] = t25;
    $[97] = t26;
  } else {
    t26 = $[97];
  }
  return t26;
}
export function EagGrid(props) {
  const $ = _c(2);
  const t0 = props as EagGridProps<readonly string[]>;
  let t1;
  if ($[0] !== t0) {
    t1 = <EagGridStoreProvider><EagGridInner {...t0} /></EagGridStoreProvider>;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}